import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode } from 'react';
import { cx, type Tone } from './cx';

// Rediseño · Bloque A. Dos piezas pequeñas que se parecen y no son lo mismo:
//   Badge — una etiqueta que INFORMA (un estado, una categoría). No se toca.
//   Chip  — un botón que FILTRA o ELIGE. Se toca y recuerda si está elegido.

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** El significado; el dominio lo decide, no se adivina por el texto. `neutral` es el gris. */
  tone?: 'neutral' | Tone;
  /** Punto de color delante del texto. Es adorno: el significado lo lleva siempre el texto. */
  dot?: boolean;
}

export function Badge({ tone = 'neutral', dot = false, className, children, ...rest }: BadgeProps) {
  return (
    <span {...rest} className={cx('n3-badge', tone !== 'neutral' && `n3-badge--${tone}`, className)}>
      {dot && <span aria-hidden="true" className="n3-badge__dot" />}
      <span>{children}</span>
    </span>
  );
}

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-pressed'> {
  /** Si está elegido. Se anuncia con `aria-pressed` y se ve con borde, fondo y letra más gruesa. */
  selected?: boolean;
  icon?: ReactNode;
  /** Cantidad junto al texto («Pendientes 3»). */
  count?: number;
}

export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function Chip(
  { selected = false, icon, count, className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button {...rest} aria-pressed={selected} className={cx('n3-chip', className)} ref={ref} type={type}>
      {icon && <span aria-hidden="true" className="n3-chip__icon">{icon}</span>}
      <span className="n3-chip__label">{children}</span>
      {count !== undefined && <span className="n3-chip__count">{count}</span>}
    </button>
  );
});
